import React, { useEffect, useState, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, Title, BarElement, Tooltip } from "chart.js";
import { fetchReportInteractions } from "../../../services/httpClient";
import { loadingCard } from "../../../helpers/loading";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface InfractionsBarChartProps {
  data?: number;
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
  isActive?: boolean;
}

export const InfractionsBarChart = ({ data, startDate, endDate, useDatePicker = false, isActive = false }: InfractionsBarChartProps) => {
  const [current, setCurrent] = useState<any>();
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (data) {
        setCurrent(data);
      } else {
        setCurrent(null);
        //default dates
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        var start = starting.toISOString().slice(0, 10);
        var end = new Date().toISOString().slice(0, 10);

        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }
        //7 day infractions
        // const start = new Date();
        // start.setDate(start.getDate() - 7);
        await getInteractions(start, end);
      }
    };

    fetchData();
  }, [data, startDate, endDate]);

  // Trigger a resize when the tab becomes active
  useEffect(() => {
    if (!isActive || !chartRef.current) return;
    const inst = chartRef.current?.chart || chartRef.current;
    if (typeof inst.resize === "function") inst.resize();
  }, [isActive]);

  const getInteractions = async (start: string, end: string) => {
    await fetchReportInteractions(start, end)
      .then((res: any) => {
        var data = res[0].data;
        var infractions = data.infraction_type_counts;
        infractions = infractions?.map((infraction: any) => {
          if (infraction?.created_at) {
            const utcDate = new Date(infraction?.created_at + " UTC");
            const pad = (n: any) => n.toString().padStart(2, "0");
            const year = utcDate.getFullYear();
            const month = pad(utcDate.getMonth() + 1);
            const day = pad(utcDate.getDate());
            const hours = pad(utcDate.getHours());
            const minutes = pad(utcDate.getMinutes());
            const seconds = pad(utcDate.getSeconds());
            const localTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            return { ...infraction, created_at_local: localTime };
          } else {
            return infraction;
          }
        });
        setCurrent(Object.values(infractions) || []);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  if (!current) {
    return loadingCard();
  }

  const chartData = {
    labels: current?.length > 0 ? current?.map((item: any) => item.infraction_type) : ["No data available"],
    datasets: [
      {
        label: "Infractions",
        data: current?.map((item: any) => item.count),
        backgroundColor: ["#5D82A0"],
      },
    ],
  };

  const maxCount = current.length ? Math.max(...current.map((item: any) => item.count)) : 0;
  const maxCountWithBuffer = Math.ceil(maxCount / 5) * 5;

  const options = {
    indexAxis: "y" as const,
    maintainAspectRatio: false,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        max: maxCountWithBuffer,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: maxCount + 50,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Infractions By Type",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: false,
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    },
  };

  return (
    <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "150px" }}>
      <Bar ref={chartRef} data={chartData} options={options} />
    </div>
  );
};
